/**
 * @solblox/shared — launch registry.
 *
 * The single source of truth mapping each **launch** (a `?launch=<id>` content
 * variant) to the on-chain **KotH season** it points at. This is the malleable
 * seam for multi-launch ops: to add a launch you append ONE entry here, then
 * the ops dashboard, client, and host CLI all pick it up.
 *
 * Pure data only (matches the "no business logic in shared" rule). PDA
 * derivation from `seasonIdHex` lives in the client/host where the web3 deps
 * are — call `seasonIdBytes()` to get the 32-byte seed and derive
 * `[SEASON_SEED, bytes]` there.
 *
 * Workflow for a new launch:
 *   1. `node host/dist/cli.js koth init-season --config config/season.prod.json
 *      --keypair <operator>` on mainnet → prints `id_hex=<64 hex chars>` (or
 *      `koth launch …` to also init+fund the vault). Do NOT use `pnpm … dev --
 *      koth …`: this pnpm forwards the `--`, so commander silently drops every
 *      option → SMOKE_PARAMS + wrong permanent admin. See STATUS.md "CLI
 *      invocation gotcha".
 *   2. Add an entry below with that `seasonIdHex`, a slug `id`, and `live: true`.
 *   3. Drop the theme bundle at `client/public/launches/<id>/theme.json`.
 */
/**
 * All known launches. Append to add one — do NOT mutate `seasonIdHex` of a
 * live launch (the season PDA is immutable; a new economic config is a new
 * launch with a new season id).
 */
export const LAUNCHES = [
    {
        id: "main",
        name: "Throne Hall — Default",
        // FILL after `koth init-season` lands on mainnet, then set live: true.
        seasonIdHex: "",
        index: 1,
        live: false,
        notes: "Default theme. Placeholder season id until mainnet init.",
    },
    {
        id: "crownclash",
        name: "Crownclash",
        seasonIdHex: "dc2a73491d39fb18f3ba6620a2820b41dee1e338fb32e9adf703fc2798bc92a4",
        index: 1,
        // LIVE 2026-06-19: token launched + verify-split PASS (vault 7000 BPS).
        live: true,
        notes: "Mainnet KotH. season_pda=SqBDGN4gwgQbtA1FbbRTtcMqoPxWRwwdJr8htpgcCeW " +
            "vault_pda=5jkoc5wvmmpRpczdgwD36c5dfgs8TMirHMwNdLEzv9gY admin=operator " +
            "FCxiH4…KtrJW. Season created 2026-06-19 (ends ~30d). " +
            "Token mint 6W314QzgwbmF6hjhPuK5LVtPK1Upx5sMBG579JcWpump (pump.fun, " +
            "verify-split PASS: vault 7000 / deployer 3000).",
    },
];
/** Look up a launch by slug. Returns undefined for unknown ids. */
export function getLaunch(id) {
    return LAUNCHES.find((l) => l.id === id);
}
/** Launches flagged live — the set the ops dashboard aggregates over. */
export function liveLaunches() {
    return LAUNCHES.filter((l) => l.live && l.seasonIdHex.length === 64);
}
/**
 * Decode a `seasonIdHex` into its 32-byte seed. Pure (no web3 dep) so it can
 * live in shared; pass the result to `PublicKey.findProgramAddressSync` in the
 * client/host. Throws on malformed hex so a typo fails loud, not silently.
 */
export function seasonIdBytes(hex) {
    if (!/^[0-9a-f]{64}$/.test(hex)) {
        throw new Error(`seasonIdHex must be 64 lowercase hex chars, got ${hex.length}: "${hex}"`);
    }
    const out = new Uint8Array(32);
    for (let i = 0; i < 32; i++) {
        out[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    }
    return out;
}
//# sourceMappingURL=launches.js.map